import { useState, useEffect, useRef } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo/logo.jpeg";
import { HomeNav, NavLinksProps } from "../Data/navLinks";
import { useAuth } from "../context/AuthContext";

interface NavbarProps {
  links?: NavLinksProps[];
}

export function Navbar({ links = HomeNav }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to top on route change (for non-hash links)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Handle cross-page and smooth hash scrolling
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const element = document.getElementById(id);
      if (element) {
        const timer = setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname, location.hash]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (href === "#") {
      e.preventDefault();
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        window.history.pushState(null, "", "/");
      } else {
        navigate("/");
        window.scrollTo(0, 0);
      }
      return;
    }

    if (href.startsWith("#")) {
      e.preventDefault();

      const id = href.slice(1);
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        window.history.pushState(null, "", href);
      } else {
        // If not found on current page, go to home page with this hash
        navigate("/" + href);
      }
    }
  };

  const getLinkPath = (href: string) => {
    const isHash = href.startsWith("#");
    if (!isHash) return href;

    // On homepage, hash links are local
    if (location.pathname === "/") {
      return href;
    }

    // If we are using the default home links, but not on home page, they belong to "/"
    if (links === HomeNav) {
      return "/" + href;
    }

    // Otherwise, they are local to the current page
    return href;
  };

  return (
    <div>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/90 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.06)]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 group"
              onClick={(e) => handleNavClick(e, "#")}
            >
              <img src={logo} alt="" className="w-12 h-12" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {links.map((link) => {
                const isHash = link.href.startsWith("#");
                return (
                  <Link
                    key={link.label}
                    to={getLinkPath(link.href)}
                    onClick={(e) => {
                      if (isHash) {
                        handleNavClick(e, link.href);
                      } else {
                        window.scrollTo(0, 0);
                      }
                    }}
                    className="px-4 py-2 rounded-full text-[13px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-200"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 500 }}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>

            {/* CTA */}
            <div className="hidden lg:flex items-center gap-3">
              {location.pathname === "/careers" ? (
                <Link
                  to="#apply"
                  onClick={(e) => handleNavClick(e, "#apply")}
                  className="px-5 py-2 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[13px] shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 hover:-translate-y-0.5 transition-all duration-200"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
                >
                  Apply Now
                </Link>
              ) : (
                <AnimatePresence mode="wait">
                  {user ? (
                    <motion.div
                      key="desktop-logged-in"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="relative"
                      ref={userMenuRef}
                    >
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500">Hi, {user.name}</p>
                        <button
                          onClick={() => setUserMenuOpen(!userMenuOpen)}
                          className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-[12px] flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer border-none outline-none"
                          style={{
                            backgroundColor: user.avatarColor,
                            fontWeight: 700,
                          }}
                        >
                          {user.name.charAt(0)}
                        </button>
                      </div>

                      <AnimatePresence>
                        {userMenuOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden flex flex-col py-1 z-50"
                          >
                            <Link
                              to="/dashboard"
                              onClick={() => {
                                setUserMenuOpen(false);
                                window.scrollTo(0, 0);
                              }}
                              className="px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors text-left cursor-pointer"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 500,
                              }}
                            >
                              Go to Dashboard
                            </Link>
                            <button
                              onClick={() => {
                                setUserMenuOpen(false);
                                handleLogout();
                              }}
                              className="px-4 py-2.5 text-[13px] text-red-600 hover:bg-red-50 transition-colors text-left w-full border-none outline-none cursor-pointer"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 500,
                              }}
                            >
                              Sign Out
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="desktop-logged-out"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center gap-3"
                    >
                      <Link
                        to="/login"
                        onClick={() => window.scrollTo(0, 0)}
                        className="text-[13px] text-slate-600 hover:text-slate-900 transition-colors"
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 500,
                        }}
                      >
                        Sign In
                      </Link>
                      <Link
                        to="/register"
                        onClick={() => window.scrollTo(0, 0)}
                        className="px-5 py-2 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[13px] shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 hover:-translate-y-0.5 transition-all duration-200"
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 600,
                        }}
                      >
                        Get Started — Free
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>

            {/* Mobile toggle */}
            <button
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? (
                <X className="w-5 h-5 text-slate-700" />
              ) : (
                <Menu className="w-5 h-5 text-slate-700" />
              )}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed top-16 left-4 right-4 z-40 bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-100 overflow-hidden"
          >
            <div className="p-4 flex flex-col gap-1">
              {links.map((link) => {
                const isHash = link.href.startsWith("#");
                return (
                  <Link
                    key={link.label}
                    to={getLinkPath(link.href)}
                    onClick={(e) => {
                      setMobileOpen(false);
                      if (isHash) {
                        handleNavClick(e, link.href);
                      } else {
                        window.scrollTo(0, 0);
                      }
                    }}
                    className="px-4 py-3 rounded-xl text-[14px] text-slate-700 hover:bg-violet-50 hover:text-violet-700 transition-all duration-200"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 500 }}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="mt-2 pt-3 border-t border-slate-100">
                {location.pathname === "/careers" ? (
                  <Link
                    to="#apply"
                    onClick={(e) => {
                      setMobileOpen(false);
                      handleNavClick(e, "#apply");
                    }}
                    className="w-full flex items-center justify-center py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] shadow-lg shadow-violet-500/25"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
                  >
                    Apply Now
                  </Link>
                ) : (
                  <AnimatePresence mode="wait">
                    {user ? (
                      <motion.div
                        key="logged-in"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="w-full flex flex-col items-center gap-2"
                      >
                        <Link
                          to="/dashboard"
                          onClick={() => {
                            setMobileOpen(false);
                            window.scrollTo(0, 0);
                          }}
                          className="w-full flex items-center justify-center py-3 rounded-xl border border-slate-200 text-white text-[14px] hover:bg-slate-50 transition-colors"
                          style={{
                            backgroundColor: user.avatarColor,
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 600,
                          }}
                        >
                          Go to Dashboard
                        </Link>

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center justify-center gap-3 px-3 py-3 rounded-xl text-[14px] text-red-500 hover:bg-red-50 transition-all cursor-pointer border border-red-500"
                        >
                          <span style={{ fontWeight: 500 }}>Sign Out</span>
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="logged-out"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="w-full flex flex-col gap-2"
                      >
                        <Link
                          to="/login"
                          onClick={() => {
                            setMobileOpen(false);
                            window.scrollTo(0, 0);
                          }}
                          className="w-full flex items-center justify-center py-3 rounded-xl border border-slate-200 text-slate-700 text-[14px]"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 600,
                          }}
                        >
                          Sign In
                        </Link>
                        <Link
                          to="/register"
                          onClick={() => {
                            setMobileOpen(false);
                            window.scrollTo(0, 0);
                          }}
                          className="w-full flex items-center justify-center py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] shadow-lg shadow-violet-500/25"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 600,
                          }}
                        >
                          Get Started — Free
                        </Link>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
